import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardHeader,
  Container,
  Grid,
  Typography,
} from '@mui/material';

import { di } from '../../../state/inversify.config';
import { MobileHeader } from '../../../components/mobileHeader/mobileHeader';
import { ListTeamDomainService } from '../listTeams/listTeamsDomainService';
import { ListTeamsQueryParamService } from '../listTeams/listTeams.queryParam.service';
import { TeamsPageQuery } from '../listTeams/listTeams.queryParams.interface';
import { GlobalQueryParams } from '../../../state/queryParams/queryParams.interface';
import { useQuerySync } from '../../../state/queryParams/useQuerySync';
import { getInitials } from '../../../helpers/initialsExtractor';
import { Allocate } from '../listTeams/components/teamAllocate.dumb';
import { AddedPlayers } from './addedPlayers';
import { AddedTeamManagers } from './addedTeamManagers';
import { Role } from '../../../state/reduxRepositories/users/users.interfaces';
import { dateTime } from '../../../time/datetime';
import { AlertDialog } from '../../../components/alertDialog/alertDialog';
import { Avatar } from '../../../components/avatar/avatar';
import { Button } from '../../../components/button/button';
import { ManageTeamDomainService } from '../team/manageTeamDomainService';
import { PlayerListTable } from '../../../pages/players/playerListTable/playerTable';
import { ConneqtOnAddPlayerDomain } from '../../../pages/players/addConneqtModal/conneqtOnAddPlayer.domain';
import { AddPlayerDomain } from '../../../pages/players/addPlayer.domain';
import { UserNotificationContext } from '../../../components/userNotification/userNotificationContext';
import { UserNotificationType } from '../../../components/userNotification/userNotificationType.enum';

export function TeamDetails() {
  const { t } = useTranslation();
  const listTeamDomainService = di.get<ListTeamDomainService>(
    ListTeamDomainService,
  );
  const manageTeamDomainService = di.get<ManageTeamDomainService>(
    ManageTeamDomainService,
  );
  const conneqtOnAddPlayerDomain = di.get<ConneqtOnAddPlayerDomain>(
    ConneqtOnAddPlayerDomain,
  );

  const addPlayerDomain = di.get<AddPlayerDomain>(AddPlayerDomain);
  const { addUserNotification } = useContext(UserNotificationContext);
  const [allocateData, setAllocateData] = useState<any[]>([]);
  const [allocateHeading, setAllocateHeading] = useState('');
  const [action, setAction] = useState<'manager' | 'players' | boolean>(false);
  const [showPlayerList, setShowPlayerList] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const teamsPageQueryService = di.get<ListTeamsQueryParamService>(
    ListTeamsQueryParamService,
  );
  const users = listTeamDomainService.useUserSelector();
  useQuerySync<TeamsPageQuery & GlobalQueryParams>(teamsPageQueryService);

  const team = listTeamDomainService.useTeamWithPlayerSelector();
  const players = listTeamDomainService.usePlayerResourceSelector();
  const allUsers = listTeamDomainService.useUserResourceSelector();
  const isUpdatingTeam = manageTeamDomainService.useTeamUpdateLoaderSelector();
  const isPlayersMode = action === 'players';

  const removeAllocation = (
    mode: 'players' | 'manager' | boolean,
    id: string,
  ): void => {
    const newIds =
      mode === 'players'
        ? team.playerIds.filter((playerId) => playerId !== id)
        : team.teamManagerUserIds?.filter(
            (teamManagerUserId) => teamManagerUserId !== id,
          );
    const clonedTeam = {
      ...team,
    };
    if (mode === 'players') {
      clonedTeam.playerIds = newIds;
    } else {
      clonedTeam.teamManagerUserIds = newIds;
    }
    manageTeamDomainService.dispatchUpdateTeam(clonedTeam);
  };

  const handleAllocatingPlayer = (ids: string[]) => {
    const clonedTeam = {
      ...team,
    };
    clonedTeam.playerIds = ids;
    manageTeamDomainService.dispatchUpdateTeam(clonedTeam);
  };

  const handleAddAllocation = (id: string): void => {
    const clonedTeam = {
      ...team,
    };
    // for team managers
    if (clonedTeam.teamManagerUserIds.includes(id)) {
      clonedTeam.teamManagerUserIds = clonedTeam.teamManagerUserIds.filter(
        (managerId) => managerId !== id,
      );
      manageTeamDomainService.dispatchUpdateTeam(clonedTeam);
    } else {
      clonedTeam.teamManagerUserIds = [...clonedTeam.teamManagerUserIds, id];
      manageTeamDomainService.dispatchUpdateTeam(clonedTeam);
    }
  };

  useEffect(() => {
    if (!isUpdatingTeam) {
      setShowPlayerList((prevState) => {
        if (prevState) {
          setShowUpdateNotification(true);
        }
        return false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatingTeam]);

  const userNotification = () => {
    addUserNotification(
      UserNotificationType.SUCCESS,
      t('SUCCESSFULLY_ADD_PLAYER_TO_TEAM', { teamName: team?.name }),
      UserNotificationType.SUCCESS,
    );
  };

  useEffect(() => {
    if (showUpdateNotification) {
      userNotification();
      setShowUpdateNotification(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpdateNotification]);

  return (
    <Container>
      <MobileHeader />
      <Card
        sx={{
          maxWidth: 300,
          boxShadow: 'none',
          background: 'transparent',
          borderRadius: 4,
        }}
        raised={false}
      >
        <CardHeader
          avatar={<Avatar title={getInitials(team?.name)} />}
          title={team.name}
          titleTypographyProps={{ variant: 'h5' }}
        />
      </Card>

      {users?.role.includes(Role.ADMIN) && (
        <Grid sx={{ ml: 2, my: 2 }}>
          <Typography variant="h6">{t('TEAM_MANAGER')}</Typography>
          <Button
            testId="addTeamManager"
            disabled={allUsers.length <= 0}
            type="submit"
            variant="contained"
            onClick={() => {
              setAction('manager');
              setAllocateData(allUsers);
              setAllocateHeading(`${t('ADD_TEAM_MANAGER')}`);
              setOpen(true);
            }}
            color="secondary"
            sx={{ my: 2 }}
            label={t('ADD_TEAM_MANAGER')}
          />

          <AddedTeamManagers
            onAddTeamManager={handleAddAllocation}
            onRemoveTeamManager={removeAllocation}
            team={team}
          />
        </Grid>
      )}
      <Grid sx={{ ml: 2, mt: 2, mb: 2 }}>
        <Typography variant="h6">{t('PLAYER')}</Typography>
        <Button
          testId="addTeamPlayer"
          disabled={players.length <= 0}
          type="submit"
          variant="contained"
          color="secondary"
          onClick={() => {
            setAction('players');
            setShowPlayerList(true);
            setAllocateHeading(`${t('ADD_PLAYER')}`);
          }}
          sx={{ my: 2 }}
          label={t('ADD_PLAYER')}
        />
        <AddedPlayers
          onAddPlayer={handleAddAllocation}
          onRemovePlayer={removeAllocation}
          team={team}
        />
      </Grid>
      {allocateData?.length > 0 && (
        <AlertDialog
          title={allocateHeading}
          open={open}
          setOpen={setOpen}
          content={
            <Box>
              {allocateData.map((data) => {
                return (
                  <Allocate
                    key={data.id}
                    isAllocated={
                      isPlayersMode
                        ? team?.playerIds.includes(data.id)
                        : team?.teamManagerUserIds.includes(data.id)
                    }
                    clearedToPlay={
                      isPlayersMode ? data?.clearedToPlay : undefined
                    }
                    allocateById={data?.id}
                    onAllocate={handleAddAllocation}
                    onRemoveAllocate={removeAllocation}
                    name={`${data?.firstName} ${data?.lastName}`}
                    mode={action}
                    bday={dateTime.format(
                      data?.dateOfBirth,
                      t('common.datetime.FORMAT_SHORT_DATE'),
                    )}
                  />
                );
              })}
            </Box>
          }
        />
      )}
      {showPlayerList && (
        <>
          <AlertDialog
            maxWidth="lg"
            title={allocateHeading}
            open={showPlayerList}
            setOpen={setShowPlayerList}
            content={
              <>
                <PlayerListTable
                  onAddPlayerOnTeam={handleAllocatingPlayer}
                  hidePlayerListActions
                  playerDomain={addPlayerDomain}
                  conneqtOnAddPlayerDomain={conneqtOnAddPlayerDomain}
                  selectedPlayers={team?.playerIds}
                  showSaveButton
                  allowPlayerSelection
                />
              </>
            }
          />
        </>
      )}
    </Container>
  );
}
